import { CommonModule } from '@angular/common';
import { Component, ContentChild, forwardRef, Input, Optional, TemplateRef } from '@angular/core';
import { ControlValueAccessor, FormsModule, NG_VALUE_ACCESSOR } from '@angular/forms';
import { MatRadioGroup, MatRadioModule } from '@angular/material/radio';

@Component({
  selector: 'si-radio-button',
  imports: [FormsModule, CommonModule, MatRadioModule],
  templateUrl: './si-radio-button.component.html',
  styleUrl: './si-radio-button.component.scss',
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => SiRadioButtonComponent),
      multi: true
    }
  ]
})
export class SiRadioButtonComponent implements ControlValueAccessor {

  private onModelChange: (value: string) => void = () => {};
  private onTouched: () => void = () => {};

  private inputDisabled = false;
  private cvaDisabled = false;

  @Input() displayText: string | undefined;
  @Input() value: string | undefined;
  @Input() isReadMode: boolean = true;
  @Input() selectedValue: string = '';
  @Input()
  set isDisabled(value: boolean) {
    this.inputDisabled = !!value;
  }
  get isDisabled(): boolean {
    return this.inputDisabled;
  }
  @ContentChild('radioContent', { read: TemplateRef }) radioBtnsTemplate!: TemplateRef<any>;

  constructor(@Optional() private readonly radioGroup: MatRadioGroup) {}

  get effectiveDisabled(): boolean {
    return this.inputDisabled || this.cvaDisabled || !!this.radioGroup?.disabled;
  }

  /** Value this button represents inside the parent group. */
  private get ownValue(): string | undefined {
    return this.value ?? this.displayText;
  }

  /**
   * `MatRadioGroup` collects its buttons through `@ContentChildren`, which cannot reach the
   * `mat-radio-button` rendered inside this wrapper's template. The group therefore never pushes
   * its value onto the buttons, and a programmatic update (`patchValue`/`setValue`) would leave the
   * previously rendered selection untouched. Deriving the selected state from the group value keeps
   * the rendered selection in sync with the form control on every change detection run.
   */
  get isSelected(): boolean {
    const currentValue = this.radioGroup ? this.radioGroup.value : this.selectedValue;
    return currentValue === this.ownValue;
  }

  ngAfterContentInit() {
    if (this.radioBtnsTemplate) {
      const radioBtnVal = this.radioBtnsTemplate.elementRef.nativeElement.value;
      this.writeValue(radioBtnVal);
    }
  }
 
  writeValue(value: string | null): void {
    this.selectedValue = value ?? '';
  }

  registerOnChange(fn: (value: string) => void): void {
    this.onModelChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.cvaDisabled = !!isDisabled;
  }

  onSelectionChanged(): void {
    const selectedValue = this.ownValue ?? '';
    this.selectedValue = selectedValue;
    this.onModelChange(selectedValue);
  }

  onBlur(): void {
    this.onTouched();
  }

}
